import { css, cx } from '@emotion/css';
import { TextareaAutosize } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

/**
 * React Flow ignores drag, pan, and wheel gestures that start in elements
 * with these classes, so the user can select text in the editor.
 */
const REACT_FLOW_IGNORE_CLASSES = 'nodrag nopan nowheel';

const styles = {
  editor: css({
    display: 'block',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 'none',
    outline: 'none',
    resize: 'none',
    overflow: 'hidden',
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    textAlign: 'inherit',
  }),
};

export interface InlineTextEditorProps {
  /** The text when editing starts. */
  value: string;
  /** The accessible name and the placeholder of the editor. */
  label: string;
  /** Saves the edited text. Enter, Tab, and a click outside the editor save. */
  onCommit: (text: string) => void;
  /** Ends the edit without a save. Escape cancels. */
  onCancel: () => void;
  /**
   * Called after Tab saves the text, to move the edit to the next field.
   * Without it, Tab moves the focus as usual and the blur saves the text.
   */
  onTab?: () => void;
  className?: string;
}

/**
 * A text box that replaces the text of a diagram element while the user
 * edits it. It takes the font and alignment of its container and grows with
 * its text, so the element keeps its look. It takes the focus and selects
 * the text when it opens.
 */
export function InlineTextEditor({
  value,
  label,
  onCommit,
  onCancel,
  onTab,
  className,
}: InlineTextEditorProps) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Enter, Tab, and Escape end the edit before the blur that follows them.
  // The blur must not end the edit again.
  const isFinished = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const finish = (save: boolean) => {
    if (isFinished.current) return;
    isFinished.current = true;
    if (save) onCommit(draft.trimEnd());
    else onCancel();
  };

  return (
    <TextareaAutosize
      ref={inputRef}
      className={cx(styles.editor, REACT_FLOW_IGNORE_CLASSES, className)}
      aria-label={label}
      placeholder={label}
      minRows={1}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(event) => {
        // Keep Backspace, Delete, and canvas shortcuts inside the editor.
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          finish(true);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          finish(false);
        } else if (event.key === 'Tab' && onTab) {
          event.preventDefault();
          finish(true);
          onTab();
        }
      }}
    />
  );
}
